import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { withRouter } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import ProLayout from '../../../components/ProLayout';
import Button from '../../../../../components/global/Button';
import useBasename from '../../../../../components/routing/useBasename';
import { AlbumForContributors } from '../../../../../graphql/queries/contributors';
import { MeQuery } from '../../../../../graphql/queries/account';
import useAlbumMembershipGroup from '../../../hooks/useAlbumMembershipGroup/useAlbumMembershipGroup';
import { isAlbumOwner } from '../../../../../util/account';
import Angle from '../../../../../components/global/Angle';

const propTypes = {
  album: PropTypes.shape({
    id: PropTypes.string,
  }),
  className: PropTypes.string,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  returnTitle: PropTypes.string,
  returnPage: PropTypes.string,
  icon: PropTypes.oneOf([
    PropTypes.string,
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  user: PropTypes.shape({
    id: PropTypes.string,
  }),
  backButtonProps: PropTypes.object,
};

const AlbumInnerPageHeader = ({
  user,
  album,
  className,
  title,
  returnTitle,
  returnPage,
  icon,
  backButtonProps,
}) => {
  const basename = useBasename();
  const isOwner = isAlbumOwner(user, album);
  const { albumMembershipGroup } = useAlbumMembershipGroup({
    entityId: album?.id,
    queryOpts: {
      skip: !album?.id || isOwner,
    },
  });

  if (!album) {
    return null;
  }

  const albumUrl = (() => {
    if (albumMembershipGroup?.id && !isOwner) {
      return `/app/${basename}/hierarchy-items/${album.id}`;
    }

    return `/app/${basename}/albums/${album.id}${returnPage || ''}`;
  })();

  const backButton = (
    <Button
      to={albumUrl}
      btnStyle="reset"
      className="btn-body-back text-extra-bold"
      {...(backButtonProps ?? {})}
    >
      <Angle direction="left" color="#0d84fe" className="mr-1" />{' '}
      {!returnTitle && <span>Back</span>}
      {returnTitle && <span>{returnTitle}</span>}
    </Button>
  );

  const classes = classNames({
    [className]: !!className,
    'px-0': true,
  });

  return (
    <Fragment>
      <div className={classes}>
        <div className="d-flex justify-content-between align-items-center w-100 mb-0">
          {backButton}
        </div>
        {title && (
          <ProLayout.MainHeader className={classes}>
            <ProLayout.Title title={title} icon={icon} />
          </ProLayout.MainHeader>
        )}
      </div>
    </Fragment>
  );
};

AlbumInnerPageHeader.propTypes = propTypes;

// Considering users should only come here via the album menu, this query should always be cached
// still, cache first is used in the rare case where user wants to land here via direct link.
const withAlbumData = graphql(AlbumForContributors, {
  options: ({ match }) => {
    return {
      variables: {
        id: match.params.albumId,
        ignore_watermark: true,
      },
      fetchPolicy: 'cache-first',
    };
  },
  skip: ({ match }) => !match.params?.albumId,
  props: ({ data: { album } }) => ({
    album,
  }),
});

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me, loading }, ownProps }) => ({
    loading: ownProps.loading || loading,
    user: me,
  }),
});

export default compose(
  withRouter,
  withMeQuery,
  withAlbumData,
)(AlbumInnerPageHeader);
